import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Gtm } from 'src/apis/gtm';
import { FunnelController } from 'src/controllers/business/FunnelController';
import { CheckoutController } from 'src/controllers/pages/CheckoutController';
import { getAppUtms } from 'src/utils/utms';

export class StripeApplePayService {
  private static instance: Stripe | null;
  private static appleAvailable: boolean = false;
  private fc: FunnelController;

  private static checkAvailability = async () => {
    try {
      StripeApplePayService.appleAvailable = true;
    } catch (e) {
      Errors.report(e, undefined, false, false);
    }
  };

  static init = async (fc: FunnelController) => {
    if (StripeApplePayService.instance) {
      return new StripeApplePayService(fc);
    }

    try {
      StripeApplePayService.instance = await loadStripe(
        window.FLAGS.pk_stripe as string,
      );
    } catch (e) {
      Errors.report(e, undefined, false, false);
    }

    await StripeApplePayService.checkAvailability();

    return new StripeApplePayService(fc);
  };

  private constructor(fc: FunnelController) {
    this.fc = fc;
  }

  private createPaymentRequest = () => {
    const country = this.fc.lead?.lead.country || window.GEO.country;
    const product = this.fc.getSelectedProduct();
    const addons = this.fc.getSelectedAddons();

    let totalAmount = Math.round(
      (product.finalPrice + product.shippingPrice) * 100,
    );
    if (addons && Array.isArray(addons)) {
      addons.forEach(addon => {
        totalAmount += Math.round(addon.finalPrice * 100);
      });
    }

    let subTotalAmount = Math.round(product.finalPrice * 100);
    if (addons && Array.isArray(addons)) {
      addons.forEach(addon => {
        subTotalAmount += Math.round(addon.finalPrice * 100);
      });
    }

    const shippingPrice = Math.round(product.shippingPrice * 100);

    return StripeApplePayService.instance?.paymentRequest({
      disableWallets: ['googlePay', 'browserCard', 'link'],
      country,
      currency: product.currencyCode.toLowerCase(),
      total: {
        label: 'Total',
        amount: totalAmount,
      },
      displayItems: [
        {
          label: 'Subtotal',
          amount: subTotalAmount,
        },
        {
          label: 'Shipping',
          amount: shippingPrice,
        },
      ],
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: 'basic',
          label: 'Regular shipping',
          detail: 'Regular shipping',
          amount: shippingPrice,
        },
      ],
    });
  };

  maybeRenderButtons = () => {
    const { stripeApplePayEnabled } = this.fc.config;

    const appleButtonCont = document.getElementById(
      'stripe-apple-pay-container',
    );
    if (appleButtonCont && stripeApplePayEnabled) {
      appleButtonCont.style.display = StripeApplePayService.appleAvailable
        ? 'block'
        : 'none';
    } else if (stripeApplePayEnabled && !appleButtonCont) {
      Errors.report(
        new Error(`StripeApayError: Apple button container not found`),
        undefined,
        false,
        false,
      );
    }
  };

  createApplePayment = async (
    checkout: CheckoutController,
    expressButton = false,
  ) => {
    if (window.DEBUG) {
      console.log(expressButton);
    }
    const elements = StripeApplePayService.instance?.elements();

    if (elements) {
      const pr = this.createPaymentRequest();
      const appleButtonCont = document.getElementById(
        'stripe-apple-pay-container',
      );

      if (appleButtonCont && pr) {
        appleButtonCont.addEventListener('click', () => {
          const auid = getAppUtms().custom_order_auid;
          const sessionId =
            new URLSearchParams(window.location.search).get('session_id') ||
            this.fc.lead.lead?.sessionId;
          const mid = this.fc.config.appleGooglePayMerchantId;
          Gtm.trackBuyButtonClick(
            this.fc.lead.lead?.customerId?.toString() || '',
            this.fc.lead.lead?.orderId || '',
            'apple_pay_express',
            auid,
            sessionId,
            mid,
          );

          const product = this.fc.getSelectedProduct();
          const addons = this.fc.getSelectedAddons();

          const productDiscountPrice = product?.discountPrice || 0;
          let totalAmount = Math.round(
            (product.finalPrice +
              product.shippingPrice -
              productDiscountPrice) *
              100,
          );
          if (addons && Array.isArray(addons)) {
            addons.forEach(addon => {
              const addonDiscountPrice = addon?.discountPrice || 0;
              totalAmount += Math.round(
                (addon.finalPrice - addonDiscountPrice) * 100,
              );
            });
          }
          let subTotalAmount = Math.round(
            (product.finalPrice - productDiscountPrice) * 100,
          );
          if (addons && Array.isArray(addons)) {
            addons.forEach(addon => {
              const addonDiscountPrice = addon?.discountPrice || 0;
              subTotalAmount += Math.round(
                (addon.finalPrice - addonDiscountPrice) * 100,
              );
            });
          }
          const shippingPrice = Math.round(product.shippingPrice * 100);
          const shippingOption =
            shippingPrice <= 0
              ? {
                  id: 'free',
                  label: 'Free shipping',
                  detail: '',
                  amount: shippingPrice,
                }
              : {
                  id: 'basic',
                  label: 'Regular shipping',
                  detail: '',
                  amount: shippingPrice,
                };

          pr.update({
            total: {
              label: 'Total',
              amount: totalAmount,
            },
            displayItems: [
              {
                label: 'Subtotal',
                amount: subTotalAmount,
              },
              {
                label: 'Shipping',
                amount: shippingPrice,
              },
            ],
            shippingOptions: [shippingOption],
          });

          pr?.show();
        });
      }

      if (pr) {
        try {
          const canMakePayment = await pr.canMakePayment();
          if (canMakePayment && canMakePayment?.applePay) {
            pr.on('token', async e => {
              const tokenData = {
                id: e.token.id,
              };
              const shippingAddress = e.shippingAddress;
              const addressLine = e.shippingAddress?.addressLine;
              const fullName = e.payerName ?? '';
              const nameParts = fullName.trim().split(' ');

              await checkout.createStripeApplePayOrder(
                JSON.stringify(tokenData),
                e,
                {
                  emailAddress: e.payerEmail ?? '',
                  familyName: nameParts[nameParts.length - 1],
                  givenName: nameParts[0] ?? '',
                  addressLines: addressLine,
                  locality: shippingAddress?.city ?? '',
                  administrativeArea: shippingAddress?.region ?? '',
                  postalCode: shippingAddress?.postalCode ?? '',
                  countryCode: shippingAddress?.country ?? '',
                  phoneNumber: e.payerPhone ?? '',
                },
              );
            });
          } else {
            const container = document.getElementById(
              'stripe-apple-pay-container',
            );
            if (container) {
              container.style.display = 'none';
            }
          }
        } catch (e) {
          Errors.report(
            e,
            {
              geo: window.GEO,
              config: this.fc.config || 'undefined',
              lead: this.fc.lead.lead || 'undefined',
              productIdx: window['productIdx'].value || 'undefined',
            },
            false,
            false,
          );
        }
      }
    }
  };
}
